import { useCallback, useEffect, useState } from "react";
import { useTheme } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import {
  toggleModal,
  selectedReport,
  setCacheRefresh,
  toggleSubmitting,
  setSaveWithProcess,
  setEvokeReportNotes,
  toggleFetchTableData,
  setReportNoteEditable,
  setReportNoteEditAccess,
  setMaximumAlertPriority,
  setAlertNotificationConfigs,
  setOriginalEvokeReportNotes,
  setShowSuggestionModal,
  setInvalidBillingDateResponse,
  toggleMissingPatientDetailModal,
} from "app/store/transmission/slice/transmissionSlice";
import { useUpdateReportNotesMutation } from "app/store/transmission/api/transmissionNotesApi";

import { isValidArray } from "utils/array";
import { formatDateTime } from "utils/date";
import { removeCache } from "app/utils/cache";
import { getInitialReportTab } from "utils/common";
import { showErrorToast, showSuccessToast } from "utils/toast";
import {
  getMaximumAlertPriority,
  getActiveAlertConfigurations,
} from "utils/notificationPreference";
import { updateAlertStatus } from "app/services/transmission/report";

import useNotesFooter from "../footer/useNotesFooter";

import {
  EVOKE_ACTIVE,
  SUBMIT_ACTIONS,
  EVOKE_AUTOMATED_NOTES,
  REPORT_NOTES_EDIT_ACCESS,
} from "static/constants";
import { formatReportNotes } from "utils/reportTemplate.util";
import { fetchReportTemplate } from "app/services/report_templates/reportNoteTemplateService";
import { shouldSendLetterWithReportNote } from "utils/report";

const useReportNotes = (rowData) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);

  const {
    reportId,
    reportDetail,
    billableDate,
    isLegacyReport,
    reportCurrentTab,
    reportAccountDetail,
    isExplicitReportNoteEditable,
    autoScrollPageNumber,
    evokeReportNotes,
    originalEvokeReportNotes,
    isReportNoteEditable,
    isUpdatingReportNotes,
    showSuggestionModal,
    // displayPopup,
    invalidBillingDateResponse,
    isNonBillable,
    showMissingPatientDetailModal,
  } = useSelector((state) => state.transmission);

  const { accountsMapper } = useSelector((state) => state.account);
  const accountAlertConfiguration =
    accountsMapper?.[rowData?.account]?.alert_configuration;

  const { currentUser: { role, user_prefs } = {} } = useSelector(
    (state) => state.auth
  );
  const { isArchiveReport } = useSelector((state) => state.archiveReport);
  // const { data: noteSuggestion } = useGetReportNoteSuggestionsQuery({
  //   reportId,
  //   noteSuggestionEnabled:
  //     rowData?.has_note_suggestions && user_prefs?.note_suggestions_enabled,
  // });

  const [combinedNotes, setCombineNotes] = useState([]);

  const [updateReportNotes] = useUpdateReportNotesMutation();

  const { handleProcess } = useNotesFooter(rowData);

  const checkReportNoteEditable = useCallback(() => {
    const { legacy, eSig } = rowData || {};

    const allowedBillingCodes = user_prefs?.allowed_billing_codes;
    const isReportOfAllowedBillingCode = isValidArray(allowedBillingCodes)
      ? reportDetail?.billingDiagnosisCodes?.every((code) =>
          allowedBillingCodes.includes(code?.billingCode)
        )
      : true;

    const reportInitialTab = getInitialReportTab({
      rowData,
      reportAccountDetail,
      isReportOfAllowedBillingCode,
    });

    const isEditableReport = !legacy && !eSig && !isArchiveReport;
    const lastReportNoteUser = reportDetail?.reportNoteUser?.name;
    const isInitialReportNote =
      !lastReportNoteUser || lastReportNoteUser === EVOKE_AUTOMATED_NOTES;
    const hasTabAccess =
      REPORT_NOTES_EDIT_ACCESS[reportCurrentTab]?.includes(role) &&
      [EVOKE_ACTIVE, reportInitialTab].includes(reportCurrentTab) &&
      isReportOfAllowedBillingCode;

    const hasReportNoteEditAccess = isEditableReport && hasTabAccess;

    const isNoteEditable =
      isExplicitReportNoteEditable ||
      (hasReportNoteEditAccess && isInitialReportNote);

    return {
      isEditable: isNoteEditable,
      hasAccess: hasReportNoteEditAccess,
    };
  }, [
    role,
    rowData,
    isArchiveReport,
    reportCurrentTab,
    reportAccountDetail,
    isExplicitReportNoteEditable,
    user_prefs?.allowed_billing_codes,
    reportDetail?.reportNoteUser?.name,
    reportDetail?.billingDiagnosisCodes,
  ]);

  useEffect(() => {
    const { isEditable, hasAccess } = checkReportNoteEditable();

    dispatch(setReportNoteEditable(isEditable));
    dispatch(setReportNoteEditAccess(hasAccess));
  }, [checkReportNoteEditable, dispatch]);

  useEffect(() => {
    formatReportNotes(combinedNotes, isReportNoteEditable);
  }, [isReportNoteEditable, combinedNotes]);

  const getReportNotes = async (evokeNotes = []) => {
    try {
      const data = await fetchReportTemplate({
        device: reportDetail?.device,
        account: reportDetail?.account,
        billingCodes: reportDetail?.billingDiagnosisCodes?.map(
          (code) => code?.billingCode
        ),
      });
      const templates = [...(data?.flatMap((obj) => obj.sections) ?? [])];

      const combinedNotes = [...templates, ...evokeNotes];
      const reportNotes = formatReportNotes(combinedNotes);
      setCombineNotes(reportNotes);

      return reportNotes;
    } catch (err) {
      showErrorToast(err?.message);
    }
  };

  const fetchReportTemplates = async () => {
    try {
      setIsLoading(true);
      const templates = await getReportNotes(rowData?.evokeNotes);
      dispatch(setEvokeReportNotes(templates));
      dispatch(
        setOriginalEvokeReportNotes(JSON.parse(JSON.stringify(templates)))
      );
    } catch (err) {
      showErrorToast(err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReportNoteAlertUpdate = async ({
    notes,
    billingDate,
    isProcessAction = false,
    shouldSendLetter = false,
  }) => {
    dispatch(setSaveWithProcess(isProcessAction));

    const reportNotes = notes.reportNotes || [];
    const maximumAlertPriority = getMaximumAlertPriority(reportNotes) || {};

    if (maximumAlertPriority) {
      dispatch(setMaximumAlertPriority(maximumAlertPriority));
    }

    const activeNotifyConfigs = getActiveAlertConfigurations(
      maximumAlertPriority,
      accountAlertConfiguration?.configurations,
      reportAccountDetail?.timezone
    );

    if (activeNotifyConfigs?.length > 0) {
      dispatch(setEvokeReportNotes(reportNotes));
      dispatch(toggleModal({ type: "alertNotification", value: true }));
      dispatch(setAlertNotificationConfigs(activeNotifyConfigs));
    } else {
      await handleReportNote({
        notes,
        billingDate,
        isProcessAction,
        shouldSendLetter,
      });
    }
  };

  const handleReportNoteSaveFlow = async ({
    notes,
    billingDate,
    isProcessAction = false,
    isAlertNotificationSending = false,
  }) => {
    dispatch(setSaveWithProcess(isProcessAction));

    const reportNotes = notes.reportNotes || [];
    dispatch(setEvokeReportNotes(reportNotes));
    // Step 1: Check for Report note suggestions
    // const suggestions = checkNoteSuggestions(reportNotes);
    // dispatch(setReportNoteSuggestions(suggestions));

    // if (suggestions?.length > 0) {
    //   // Show Report note suggestion modal if suggestions exist
    //   dispatch(setShowSuggestionModal({ showModal: true, displayPopup: true }));
    // } else

    const { hasLetterSendEligibility, isValidForSendingLetter } =
      shouldSendLetterWithReportNote({
        reportTab: reportCurrentTab,
        billableDate,
        report: reportDetail,
      });

    if (hasLetterSendEligibility && isProcessAction) {
      dispatch(toggleMissingPatientDetailModal(true));
    } else if (
      isAlertNotificationSending &&
      accountAlertConfiguration &&
      accountAlertConfiguration?.account === rowData?.account &&
      reportCurrentTab === EVOKE_ACTIVE
    ) {
      // Step 2: Check for alert notifications
      await handleReportNoteAlertUpdate({
        notes,
        billingDate,
        isProcessAction,
        shouldSendLetter: isValidForSendingLetter,
      });
    } else {
      // Step 3: Proceed with save/save&process API call

      await handleReportNote({
        notes,
        billingDate,
        isProcessAction,
        shouldSendLetter: isValidForSendingLetter,
      });
    }
  };

  const handleReportNote = async ({
    notes,
    billingDate,
    isProcessAction = false,
    isArchive = false,
    isAlertStatusUpdate = false,
    alertNotifications,
    skipAlertNotification,
    shouldSendLetter = false,
  }) => {
    const checkBillingDate = Boolean(
      billingDate &&
      !isAlertStatusUpdate &&
      (!invalidBillingDateResponse ||
        (invalidBillingDateResponse &&
          formatDateTime(billingDate) !==
            invalidBillingDateResponse?.billingDate))
    );

    const reportNotes = notes?.reportNotes || [];

    // const hasNoteSuggestions = noteSuggestion?.ai_response?.length > 0;
    // const suggestions = checkNoteSuggestions(reportNotes);

    const submitAction = isArchive
      ? SUBMIT_ACTIONS.ARCHIVE
      : isProcessAction
        ? SUBMIT_ACTIONS.SAVE_AND_PROCESS
        : SUBMIT_ACTIONS.SAVE;

    try {
      dispatch(
        toggleSubmitting({ type: "reportNotes", value: true, submitAction })
      );

      const payload = {
        reportId,
        evokeNotes: reportNotes,
        billingDate: billingDate ? formatDateTime(billingDate) : null,
        autoScrollPageNumber,
        isAlertStatusUpdate,
        alertNotifications,
        skipAlertNotification,
        checkBillingDate,
        nonBillable: isNonBillable,
        isArchive,
      };

      const result = await updateReportNotes({ ...payload }).unwrap();

      if (
        checkBillingDate &&
        result.type === "Error" &&
        result?.data?.isValidBillingDate === false
      ) {
        setTimeout(() => {
          dispatch(setReportNoteEditable(true));
          dispatch(setInvalidBillingDateResponse(result?.data));
        }, 5);
      } else if (result?.data) {
        const isProcessing = isProcessAction || isArchive;
        if (!isProcessing) {
          showSuccessToast(result?.message);
        }

        dispatch(
          selectedReport({
            id: reportId,
            isLegacyReport: isLegacyReport,
            reportDetail: result?.data,
          })
        );
        setTimeout(() => {
          dispatch(setReportNoteEditable(false));
          dispatch(setInvalidBillingDateResponse(null));
        }, 5);

        const updatedTemplates = await getReportNotes(result?.data?.evokeNotes);
        dispatch(setEvokeReportNotes(updatedTemplates));
        dispatch(
          setOriginalEvokeReportNotes(
            JSON.parse(JSON.stringify(updatedTemplates))
          )
        );

        if (isArchive) {
          await updateAlertStatus(reportId, "none");
        }

        if (isProcessing) {
          await handleProcess(
            "process",
            false,
            true,
            isArchive,
            false,
            shouldSendLetter
          );
        } else {
          await removeCache();
          await dispatch(toggleFetchTableData(true));
          await dispatch(setCacheRefresh(true));
        }
      }
    } catch (err) {
      dispatch(setReportNoteEditable(true));
      showErrorToast(err?.message);
    } finally {
      dispatch(
        toggleSubmitting({ type: "reportNotes", value: false, submitAction })
      );
      dispatch(toggleFetchTableData(false));
      dispatch(
        setShowSuggestionModal({ showModal: false, displayPopup: false })
      );
    }
  };

  // const checkNoteSuggestions = (notes) => {
  //   if (
  //     user_prefs?.note_suggestions_enabled &&
  //     reportDetail?.manufacturer === "Medtronic" &&
  //     reportDetail?.device_category === "ILR" &&
  //     noteSuggestion?.ai_response?.length > 0
  //   ) {
  //     const grouped = noteSuggestion?.ai_response?.reduce((acc, suggestion) => {
  //       const { label, alert, value, pageNumber, totalEpisodes } = suggestion;
  //       if (!acc[label]) {
  //         acc[label] = {
  //           label,
  //           alerts: new Set(),
  //           values: [],
  //           pages: [],
  //           episodes: [],
  //         };
  //       }
  //       acc[label].alerts.add(alert);
  //       acc[label].values.push(value);
  //       acc[label].pages.push(pageNumber);
  //       acc[label].episodes.push(totalEpisodes);
  //       return acc;
  //     }, {});

  //     return Object.entries(grouped).flatMap(([label, group]) => {
  //       return notes
  //         .filter(
  //           ({ note, content }) =>
  //             note === label &&
  //             [...group.alerts].every((alert) =>
  //               content.every(
  //                 (c) => c?.alert?.toLowerCase() !== alert?.toLowerCase()
  //               )
  //             )
  //         )
  //         .map((note) => ({
  //           ...note,
  //           identifiedAlerts: [...group.alerts],
  //           values: group.values,
  //           pageNumbers: [...group.pages],
  //           episodes: group.episodes,
  //           content:
  //             note?.content?.length > 0
  //               ? note?.content
  //               : [{ description: "", alert: "None", pageNumber: null }],
  //         }));
  //     });
  //   }
  // };

  return {
    theme,
    reportId,
    isLoading,
    billableDate,
    evokeReportNotes,
    reportAccountDetail,
    isUpdatingReportNotes,
    originalEvokeReportNotes,

    dispatch,
    handleReportNote,
    fetchReportTemplates,
    handleReportNoteAlertUpdate,
    // noteSuggestion,

    showSuggestionModal,
    handleReportNoteSaveFlow,
    showMissingPatientDetailModal,
  };
};

export default useReportNotes;
